import { createContext, useEffect, useState } from 'react'
import {
   AccountStatementBannerProps,
   BaseOfferRequest,
   saveAccountBannerConfig,
   sendTryOnEmail,
   StatementAccount,
} from '../../../api/statementAccount'
import useGetAccountBannerConfig from '../../../hooks/useGetAccountBannerConfig/useGetAccountBannerConfig'
import { Image } from 'ui/fields/Photo/components/PhotoSelector'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useLocation } from 'react-router-dom'
import { getParamsValues } from '../../../utils/statementBannerUtils'
import useGetPartnerStatementAccount from '../../../hooks/useGetPartnerStatementAccount/useGetPartnerStatementAccount'
import useGetLoyaltyPlansForStatement from '../../../hooks/useGetLoyaltyPlansForStatement/useLoyaltyPlansForStatement'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'

type AccountStatementContextType = {
   config: AccountStatementBannerProps | null
   isLoading: boolean
   setImage: (image: any) => void
   image: Image | null
   member?: StatementAccount
   programs?: Array<any>
   loyaltyPlanId: string | undefined
   setLoyaltyPlanId: (loyaltyPlanId: string | undefined) => void
   onSubmit: (data: any) => void
   handleSendTryOnEmail: (emails: string, id: string, cb: () => void) => void
}
export const AccountStatementContext = createContext<AccountStatementContextType>({
   config: null,
   isLoading: false,
   setImage: () => {},
   image: null,
   member: undefined,
   programs: [],
   loyaltyPlanId: undefined,
   setLoyaltyPlanId: () => {},
   onSubmit: () => {},
   handleSendTryOnEmail: () => {},
})

export const AccountStatementProvider = ({ children }: { children: React.ReactNode }) => {
   const location = useLocation()
   const intl = useIntl()
   const paramValue = getParamsValues(location.search)
   const [loyaltyPlanId, setLoyaltyPlanId] = useState<string | undefined>(undefined)
   const { data: config, isLoading, refetch } = useGetAccountBannerConfig({ loyaltyPlanId })
   const { data: member } = useGetPartnerStatementAccount({ externalId: paramValue.id, loyaltyPlanId })
   const { data: programs, isLoading: isProgramListLoading } = useGetLoyaltyPlansForStatement({})
   const [image, setImage] = useState<any | null>(config?.image ?? null)
   const [isLoadingSave, setIsLoadingSave] = useState(false)
   const { mutate: submitForm } = useMutation({
      mutationFn: saveAccountBannerConfig,
   })
   const { mutate: submitEmail } = useMutation({
      mutationFn: sendTryOnEmail,
   })
   useEffect(() => {
      if (!image && config?.image && config?.image.path) {
         setImage(config.image)
      }
   }, [config, image])
   useEffect(() => {
      if (loyaltyPlanId === undefined && programs && programs?.length > 0) {
         const defaultLoyaltyPlanId = programs.find((program: any) => program.isDefault)?.id
         setLoyaltyPlanId(defaultLoyaltyPlanId)
      }
   }, [programs, loyaltyPlanId])
   const onSubmit = (data: any) => {
      setIsLoadingSave(true)
      const { url, showLinkURL, showBanner, image, ...rest } = data
      const link = showLinkURL ? url : null
      const imagereq = showBanner ? image : null
      let requestData: BaseOfferRequest = {
         image: imagereq,
         loyaltyPlanId: loyaltyPlanId,
         link: link,
         isLinkEnabled: showLinkURL,
         isActive: showBanner,
         ...rest,
      }
      submitForm(requestData, {
         onSuccess: () => {
            config?.id
               ? toast.success(intl.formatMessage({ id: 'settings.submit.success.update' }))
               : toast.success(intl.formatMessage({ id: 'settings.submit.success' }))

            setIsLoadingSave(false)
            refetch()
         },
         onError: (error: any) => {
            setIsLoadingSave(false)
            const errorData = error?.response?.data
            const fieldErrors = errorData?.fields
            const generalError = errorData?.error
            const errorMessage = fieldErrors?.length
               ? fieldErrors.map((fieldError: any) => fieldError.message).join(', ')
               : generalError || intl.formatMessage({ id: 'settings.submit.error' })

            toast.error(errorMessage)
            console.log('error', error)
         },
      })
   }
   const handleSendTryOnEmail = (emails: string, id: string, cb: (status: boolean) => void) => {
      setIsLoadingSave(true)
      submitEmail(
         {
            emails: emails,
            loyaltyPlanId: loyaltyPlanId,
            id: id,
         },
         {
            onSuccess: () => {
               cb(true)
               setIsLoadingSave(false)
               toast.success(intl.formatMessage({ id: 'settings.tryon.success' }))
            },
            onError: (error: any) => {
               toast.error(
                  error?.response?.data?.error
                     ? error.response.data.error
                     : intl.formatMessage({ id: 'settings.tryon.error' }),
               )
               setIsLoadingSave(false)
               console.log('error', error)
            },
         },
      )
   }
   return (
      <AccountStatementContext.Provider
         value={{
            config: config || null,
            isLoading: isLoadingSave,
            setImage,
            image,
            member,
            programs: programs,
            setLoyaltyPlanId,
            loyaltyPlanId,
            onSubmit,
            handleSendTryOnEmail,
         }}
      >
         {isLoading || isProgramListLoading ? (
            <div className="absolute inset-0 bg-neutral-400 opacity-25 flex items-center justify-center">
               <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
         ) : (
            (!isLoading || isProgramListLoading) && children
         )}
      </AccountStatementContext.Provider>
   )
}
